import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BarChart3, LineChart as LineIcon, PieChart as PieIcon, ScatterChart as ScatterIcon, AreaChart as AreaIcon, ArrowLeft, ArrowRight, Plug, FileText, Upload, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileDropzone } from "@/components/ui/FileDropzone";
import { parseUserFile, type ParsedFile } from "@/lib/file-parsing";
import { useSavedCharts } from "@/hooks/useSavedCharts";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ScatterChart, Scatter, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

type ChartType = "bar" | "line" | "pie" | "scatter" | "area";

const CHART_TYPES: { value: ChartType; label: string; icon: any }[] = [
  { value: "bar", label: "Bar", icon: BarChart3 },
  { value: "line", label: "Line", icon: LineIcon },
  { value: "pie", label: "Pie", icon: PieIcon },
  { value: "scatter", label: "Scatter", icon: ScatterIcon },
  { value: "area", label: "Area", icon: AreaIcon },
];

const COLORS = ["hsl(var(--primary))", "#7c3aed", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"];

export const DashboardGenerator = () => {
  const { saveChart } = useSavedCharts();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [chartType, setChartType] = useState<ChartType>("bar");

  const [sourceMode, setSourceMode] = useState<"crm" | "file" | "upload" | "">("");
  const [crms, setCrms] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [selectedCrm, setSelectedCrm] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [loadingSources, setLoadingSources] = useState(false);

  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [xAxis, setXAxis] = useState<string>("");
  const [yAxis, setYAxis] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (step === 3) loadSources();
  }, [step]);

  const loadSources = async () => {
    setLoadingSources(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const [{ data: crmRows }, { data: fileRows }] = await Promise.all([
        supabase.from("crm_integrations").select("id, name, crm_type, status").eq("user_id", user.id).eq("status", "active"),
        supabase.from("file_storage").select("id, file_name, file_path, mime_type").eq("user_id", user.id).eq("is_folder", false).order("created_at", { ascending: false }).limit(50),
      ]);
      setCrms(crmRows ?? []);
      setFiles(fileRows ?? []);
    } finally {
      setLoadingSources(false);
    }
  };

  const loadFromStorage = async (fileId: string) => {
    const file = files.find((f) => f.id === fileId);
    if (!file) return;
    setLoadingSources(true);
    try {
      const { data, error } = await supabase.storage.from("user_files").download(file.file_path);
      if (error) throw error;
      const f = new File([data], file.file_name, { type: file.mime_type });
      const p = await parseUserFile(f);
      setParsed(p);
    } catch (e: any) {
      toast.error(`Failed to load file: ${e.message}`);
    } finally {
      setLoadingSources(false);
    }
  };

  const numericRows = useMemo(() => {
    if (!parsed?.rows || !xAxis || !yAxis) return [];
    return parsed.rows
      .map((r) => ({ [xAxis]: r[xAxis], [yAxis]: Number(r[yAxis]) }))
      .filter((r) => r[xAxis] !== null && r[xAxis] !== undefined && !Number.isNaN(r[yAxis]));
  }, [parsed, xAxis, yAxis]);

  const canFinish = numericRows.length > 0;

  const next = () => {
    setError("");
    if (step === 1 && !title.trim()) return setError("Enter a chart title");
    if (step === 3) {
      if (sourceMode === "crm" && !selectedCrm) return setError("Select a CRM");
      if (sourceMode === "file" && !selectedFile) return setError("Select a file");
      if (sourceMode === "upload" && !parsed) return setError("Upload a file");
      if (sourceMode === "crm") return setError("CRM-as-source for charts will preview once CRM data is synced. Upload a file or pick an uploaded file for now.");
    }
    setStep(step + 1);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Chart — Step {step} of 5</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {step === 1 && (
          <div className="space-y-2">
            <Label>Chart title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Monthly Revenue" />
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-3 md:grid-cols-5">
            {CHART_TYPES.map((t) => {
              const Icon = t.icon;
              const active = chartType === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setChartType(t.value)}
                  className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition ${active ? "border-primary bg-primary/5" : "border-muted hover:border-primary/50"}`}
                >
                  <Icon className="h-8 w-8" />
                  <span className="text-sm font-medium">{t.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="grid gap-2 md:grid-cols-3">
              <SourceTile icon={Plug} label="From CRM" active={sourceMode === "crm"} onClick={() => setSourceMode("crm")} />
              <SourceTile icon={FileText} label="Uploaded files" active={sourceMode === "file"} onClick={() => setSourceMode("file")} />
              <SourceTile icon={Upload} label="Upload from device" active={sourceMode === "upload"} onClick={() => setSourceMode("upload")} />
            </div>

            {sourceMode === "crm" && (
              <div>
                {loadingSources ? <Loader2 className="animate-spin h-4 w-4" /> : crms.length === 0 ? (
                  <Alert>
                    <AlertDescription>
                      No CRM connected.{" "}
                      <Link to="/data" className="underline text-primary">Connect one in Integrations</Link>.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Select value={selectedCrm} onValueChange={setSelectedCrm}>
                    <SelectTrigger><SelectValue placeholder="Choose CRM" /></SelectTrigger>
                    <SelectContent>
                      {crms.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.crm_type})</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {sourceMode === "file" && (
              <div>
                {loadingSources ? <Loader2 className="animate-spin h-4 w-4" /> : files.length === 0 ? (
                  <Alert><AlertDescription>No uploaded files yet.</AlertDescription></Alert>
                ) : (
                  <Select value={selectedFile} onValueChange={async (v) => { setSelectedFile(v); await loadFromStorage(v); }}>
                    <SelectTrigger><SelectValue placeholder="Choose file" /></SelectTrigger>
                    <SelectContent>
                      {files.map((f) => <SelectItem key={f.id} value={f.id}>{f.file_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {sourceMode === "upload" && (
              <FileDropzone onParsed={(p) => setParsed(p)} label="Upload data" />
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            {!parsed ? (
              <Alert><AlertDescription>No data loaded — go back and choose a source.</AlertDescription></Alert>
            ) : parsed.kind !== "table" ? (
              <Alert variant="destructive"><AlertDescription>This file isn't tabular. Use CSV/Excel for charts.</AlertDescription></Alert>
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label>X axis</Label>
                    <Select value={xAxis} onValueChange={setXAxis}>
                      <SelectTrigger><SelectValue placeholder="Pick column" /></SelectTrigger>
                      <SelectContent>
                        {parsed.columns?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Y axis (numeric)</Label>
                    <Select value={yAxis} onValueChange={setYAxis}>
                      <SelectTrigger><SelectValue placeholder="Pick column" /></SelectTrigger>
                      <SelectContent>
                        {parsed.columns?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="rounded-lg border max-h-48 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted"><tr>{parsed.columns?.map((c) => <th key={c} className="p-1 text-left">{c}</th>)}</tr></thead>
                    <tbody>
                      {parsed.rows?.slice(0, 10).map((r, i) => (
                        <tr key={i} className="border-t">
                          {parsed.columns?.map((c) => <td key={c} className="p-1">{String(r[c] ?? "")}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3">
            <h3 className="font-semibold">{title}</h3>
            {!canFinish ? (
              <Alert variant="destructive">
                <AlertDescription>
                  No valid numeric data available for the selected columns. Pick different columns or fix your data.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="h-80 w-full">
                <ResponsiveContainer>
                  {renderChart(chartType, numericRows, xAxis, yAxis)}
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="flex justify-between pt-3 border-t">
          <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < 5 ? (
            <Button onClick={next}>Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
          ) : (
            <Button
              disabled={!canFinish || saveChart.isPending}
              onClick={async () => {
                await saveChart.mutateAsync({
                  title: title.trim(),
                  chartType,
                  xField: xAxis,
                  yField: yAxis,
                  data: numericRows,
                  origin: "generator",
                  sourceType: sourceMode || null,
                  sourceName: parsed?.fileName ?? null,
                  sourceId: selectedFile || selectedCrm || null,
                });
                toast.success("Chart saved to your dashboard");
              }}
            >
              {saveChart.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Save to dashboard
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SourceTile = ({ icon: Icon, label, active, onClick }: any) => (
  <button onClick={onClick} className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg ${active ? "border-primary bg-primary/5" : "border-muted hover:border-primary/50"}`}>
    <Icon className="h-6 w-6" />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

function renderChart(type: ChartType, data: any[], x: string, y: string): any {
  switch (type) {
    case "bar":
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={x} /><YAxis /><Tooltip /><Legend />
          <Bar dataKey={y} fill={COLORS[0]} />
        </BarChart>
      );
    case "line":
      return (
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={x} /><YAxis /><Tooltip /><Legend />
          <Line type="monotone" dataKey={y} stroke={COLORS[0]} />
        </LineChart>
      );
    case "area":
      return (
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={x} /><YAxis /><Tooltip /><Legend />
          <Area type="monotone" dataKey={y} stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.3} />
        </AreaChart>
      );
    case "scatter":
      return (
        <ScatterChart>
          <CartesianGrid />
          <XAxis dataKey={x} /><YAxis dataKey={y} /><Tooltip cursor={{ strokeDasharray: "3 3" }} />
          <Scatter data={data} fill={COLORS[0]} />
        </ScatterChart>
      );
    case "pie":
      return (
        <PieChart>
          <Tooltip /><Legend />
          <Pie data={data} dataKey={y} nameKey={x} label outerRadius={120}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
        </PieChart>
      );
  }
}
